import classNames from 'classnames';
import { Fragment, type ReactNode } from 'react';
import { AccordionContentElement, AccordionElement, AccordionTitleElement } from '../accordionElement';
import { AuthorImagesElement } from '../authorImagesElement';
import { AuthorsElement } from '../authorsElement/Authors';
import { BlockquoteElement, CaptionElement, QuoteElement } from '../blockquoteElement';
import { BreadcrumbsElement } from '../breadcrumbsElement';
import { ButtonElement } from '../buttonElement';
import { CodeBlockElement } from '../codeBlockElement';
import { ColumnElement } from '../columnElement';
import { ColumnsElement } from '../columnsElement';
import { ContainerElement } from '../containerElement';
import { ContentGateElement } from '../contentGateElement';
import { DividerElement } from '../dividerElement';
import { EmbedElement } from '../embedElement';
import { FigcaptionElement } from '../figcaptionElement';
import { FooterElement } from '../footerElement';
import {
  GenericEmbedDescriptionElement,
  GenericEmbedElement,
  GenericEmbedHostElement,
  GenericEmbedTitleElement,
} from '../genericEmbedElement/GenericEmbed';
import {
  Heading1Element,
  Heading2Element,
  Heading3Element,
  Heading4Element,
  Heading5Element,
  Heading6Element,
} from '../headingElement';
import { IconElement } from '../iconElement';
import { ImageElement } from '../imageElement';
import { BulletedListElement, NumberedListElement } from '../listElement';
import { ListItemElement } from '../listItemElement';
import { LoginElement } from '../loginElement';
import { ParagraphSerializer } from '../paragraphElement';
import { PostCommentFormElement } from '../postCommentFormElement';
import { PostCommentsElement } from '../postCommentsElement';
import { PostCommentsFeedElement } from '../postCommentsFeedElement';
import { PostElement } from '../postElement/Post';
import { PostShareElement } from '../postShareElement';
import { PricingElement } from '../pricingElement';
import { RecommendationsElement } from '../recommendationsElement/Recommendations';
import { ResetPasswordElement } from '../resetPasswordElement';
import { RootElement } from '../rootElement';
import { SectionElement } from '../sectionElement';
import { SignupElement } from '../signupElement';
import { SignupModalElement } from '../signupModalElement';
import { SocialsElement } from '../socialsElement';
import { SpacerElement } from '../spacerElement';
import { SurveyElement } from '../surveyElement';
import { SwitchElement } from '../switchElement';
import { TagsElement } from '../tagsElement/Tags';
import { TestimonialsElement } from '../testimonialsElement/Testimonials';
import { SerializeTextLeaf } from '../textLeaf';
import { UpdatePasswordElement } from '../updatePasswordElement';
import type { GetElementProps } from './Serializer.types';

const componentMap: Record<string, React.ComponentType<any>> = {
  blockquoteFigure: BlockquoteElement,
  button: ButtonElement,
  breadcrumbs: BreadcrumbsElement,
  codeBlock: CodeBlockElement,
  columns: ColumnsElement,
  column: ColumnElement,
  container: ContainerElement,
  divider: DividerElement,
  figcaption: FigcaptionElement,
  footer: FooterElement,
  image: ImageElement,
  signupModal: SignupModalElement,
  listItem: ListItemElement,
  orderedList: NumberedListElement,
  bulletList: BulletedListElement,
  paragraph: ParagraphSerializer,
  spacer: SpacerElement,
  quote: QuoteElement,
  quoteCaption: CaptionElement,
  icon: IconElement,
  genericEmbed: GenericEmbedElement,
  genericEmbedTitle: GenericEmbedTitleElement,
  genericEmbedDescription: GenericEmbedDescriptionElement,
  genericEmbedHost: GenericEmbedHostElement,
  accordion: AccordionElement,
  accordionTitle: AccordionTitleElement,
  accordionContent: AccordionContentElement,
  section: SectionElement,
  contentGate: ContentGateElement,
  post: PostElement,
  signup: SignupElement,
  recommendations: RecommendationsElement,
  testimonials: TestimonialsElement,
  authors: AuthorsElement,
  survey: SurveyElement,
  resetPassword: ResetPasswordElement,
  socials: SocialsElement,
  tags: TagsElement,
  updatePassword: UpdatePasswordElement,
  login: LoginElement,
  pricing: PricingElement,
  postComments: PostCommentsElement,
  postCommentForm: PostCommentFormElement,
  postCommentsFeed: PostCommentsFeedElement,
  embed: EmbedElement,
  authorImages: AuthorImagesElement,
  switch: SwitchElement,
  postShare: PostShareElement,
};

export function getElement({ node, rootClassName, rootStyle, children, key }: GetElementProps): ReactNode {
  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node} />;
  }

  let Comp: any;

  switch (node.type) {
    case 'paragraph':
    case 'listItem':
    case 'bulletList':
    case 'orderedList':
    case 'codeBlock':
    case 'spacer':
    case 'divider':
    case 'image':
    case 'figcaption':
    case 'blockquoteFigure':
    case 'quote':
    case 'quoteCaption':
    case 'button':
    case 'columns':
    case 'column':
    case 'container':
    case 'footer':
    case 'signupModal':
    case 'genericEmbed':
    case 'genericEmbedTitle':
    case 'genericEmbedDescription':
    case 'genericEmbedHost':
    case 'accordion':
    case 'recommendations':
    case 'testimonials':
    case 'authors':
    case 'survey':
    case 'resetPassword':
    case 'socials':
    case 'tags':
    case 'switch':
    case 'updatePassword':
    case 'login':
    case 'pricing':
    case 'postComments':
    case 'embed':
    case 'accordionTitle':
    case 'accordionContent':
    case 'post':
    case 'postShare':
    case 'signup':
    case 'section':
    case 'icon':
    case 'authorImages':
    case 'breadcrumbs':
      Comp = componentMap[node.type as keyof typeof componentMap];

      return (
        <Comp key={key} element={node} className={classNames(rootClassName, `dream-${node.type}`)}>
          {children}
        </Comp>
      );
    case 'heading': {
      const level = node.attrs?.level as number;

      switch (level) {
        case 1:
          Comp = Heading1Element;
          break;
        case 2:
          Comp = Heading2Element;
          break;
        case 3:
          Comp = Heading3Element;
          break;
        case 4:
          Comp = Heading4Element;
          break;
        case 5:
          Comp = Heading5Element;
          break;
        default:
          Comp = Heading6Element;
      }

      return (
        <Comp key={key} element={node} className={`dream-${node.type}`}>
          {children}
        </Comp>
      );
    }
    case 'doc': {
      return (
        <RootElement element={node} className={classNames(rootClassName, `dream-${node.type}`)} style={rootStyle}>
          {children}
        </RootElement>
      );
    }
    case 'hardBreak':
      return <br />;
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}
